import { FC, useMemo } from 'react';
import { IAccountTable } from 'models/Account';
import { useHistory } from 'react-router-dom';
import { Column, MTableToolbar, Options } from 'material-table';
import Table from 'components/widgets/table';
import AddAccountButton from './AddAccountBtn';
import AccountIdCell from './AccountIdCell';
import { matchesAccountOrNetwork } from './search';
import { formatToTableStructure } from './utils';
import Tooltip from '../../../widgets/tooltip';
import { isLoading, selectAccounts } from '../../../../redux/slicers/accounts';
import { useAppDispatch, useAppSelector } from 'redux/store';

const options: Options<object> = {
    filtering: true,
    search: true,
    pageSize: 20,
    columnsButton: true,
    exportButton: { csv: true, pdf: false }, // pdf disabled: jspdf is vulnerable (CVE-2026-25755) and only the pdf export path invokes it; csv export is native and safe
    exportAllData: true,
    tableLayout: 'fixed',
    doubleHorizontalScroll: true,
};

const AccountTable: FC = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { accounts = [] } = useAppSelector(selectAccounts);
    const salesforceAccounts = useAppSelector((state) => state.accounts.salesforceAccounts);
    const { regions, dataLocations } = useAppSelector((state) => state.lookUps);
    const loading: boolean = dispatch(isLoading('initAccounts')) || dispatch(isLoading('createAccount'));

    const tableData = useMemo(
        () => accounts.map((acc) => formatToTableStructure(acc, regions, dataLocations, salesforceAccounts)),
        [accounts, regions, dataLocations, salesforceAccounts]
    );

    const handleRowClick = (_a: unknown, rowData: IAccountTable) => {
        if (rowData.id) {
            history.push(`/accounts/view/${rowData.id}`);
        }
    };

    const COLUMNS: Column<IAccountTable>[] = [
        {
            title: 'Name',
            field: 'name',
            removable: false,
            render: ({ name }) => (
                <Tooltip title={name} disable={false}>
                    <div className="ellipsis">{name}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Network ID',
            // field stays networkId: it is what sorting and CSV export read, and it holds the
            // primary (lowest/oldest) network. The networks list is reached via customFilterAndSearch,
            // which material-table consults first for both the column filter and the global search.
            field: 'networkId',
            filterPlaceholder: 'Network ID or any network',
            render: (row) => <AccountIdCell account={row} />,
            customFilterAndSearch: (term, row) => matchesAccountOrNetwork(term, row),
        },
        {
            title: 'Region',
            field: 'region',
            render: ({ region }) => (
                <Tooltip title={region} disable={false}>
                    <div className="ellipsis">{region}</div>
                </Tooltip>
            ),
        },
        {
            title: 'SE',
            field: 'seName',
            render: ({ seName }) => (
                <Tooltip title={seName} disable={false}>
                    <div className="ellipsis">{seName}</div>
                </Tooltip>
            ),
        },
        {
            title: 'CSM',
            field: 'csmName',
            render: ({ csmName }) => (
                <Tooltip title={csmName} disable={false}>
                    <div className="ellipsis">{csmName}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Data Location',
            field: 'dataLocation',
            render: ({ dataLocation }) => (
                <Tooltip title={dataLocation} disable={false}>
                    <div className="ellipsis">{dataLocation}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Creation Date',
            field: 'createdDate',
            type: 'date',
            render: ({ createdDate }) => (
                <Tooltip title={createdDate?.toString()} disable={false}>
                    <div className="ellipsis">{createdDate}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Active',
            field: 'isActive',
            render: ({ isActive }) => (
                <Tooltip title={isActive} disable={false}>
                    <div className="ellipsis">{isActive}</div>
                </Tooltip>
            ),
        },
    ];

    return (
        <Table
            columns={COLUMNS}
            rows={tableData}
            options={options}
            isLoading={loading}
            handleRowClick={handleRowClick}
            customComponents={{
                Toolbar: (props) => (
                    <div className="table-toolbar">
                        <MTableToolbar {...props} />
                        <AddAccountButton className="add-account-btn" label="Add Account" dest="/accounts/create/" />
                    </div>
                ),
            }}
        />
    );
};

export default AccountTable;
